/**
 * RuntimeGroupTreeView — virtualized 1C-style lazy group tree.
 *
 * Renders the flattened `entries` from `useGroupTree` through ONE virtualizer:
 * group-header rows (chevron + formatted label + count + per-numeric-column
 * sums) and windowed leaf rows that render IDENTICALLY to the flat list via the
 * shared `ListDataRow`. Rows are fetched lazily per expanded leaf group.
 */
import React, { useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Skeleton from '@mui/material/Skeleton'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useVirtualizer } from '@tanstack/react-virtual'
import { renderListCell, monoSx } from '@/shared/listCell'
import { widthSx, type ListColumnSpec } from './listSchema'
import { isNumericListColumn, type ListCellAttrHint } from '@/shared/formatListCellValue'
import { resolveNumberDigits, formatNumberDisplay } from '@/runtime/number/formatNumber'
import { LIST_ROW_OVERFLOW_COL_WIDTH } from '@/shared/listTable'
import { listCellHairlineSx, listDensityCellSx, listDensityControlSx, listHeaderStickySx, listNumericBodySx } from '@/shared/listTable/listTableChrome'
import { useDensity } from '@/shared/density/DensityProvider'
import type { FlatEntry, GroupPath } from '@/shared/listViews/groupTreeModel'
import type { RowState } from './windowedRowCache'
import { ListDataRow, type ListRowPrint } from './ListDataRow'
import { ListEmptyState } from '@/shared/listViews/ListEmptyState'

export interface RuntimeGroupTreeViewProps {
  entries: FlatEntry[]
  columns: ListColumnSpec[]
  hintFor: (col: ListColumnSpec) => ListCellAttrHint | undefined
  rowHeight: number
  toggle: (path: GroupPath) => void
  rowAt: (entry: Extract<FlatEntry, { kind: 'row' }>) => RowState
  groupValues: (path: GroupPath) => Record<string, unknown> | undefined
  print: ListRowPrint
  onEdit: (row: Record<string, any>) => void
  onDelete: (recordId: string, wasMarked?: boolean) => void
  onUndelete: (recordId: string) => void
  /** Effective delete permission (Task 12). Defaults to true — see ListDataRow. */
  canDelete?: boolean
}

export const RuntimeGroupTreeView: React.FC<RuntimeGroupTreeViewProps> = ({
  entries,
  columns,
  hintFor,
  rowHeight,
  toggle,
  rowAt,
  groupValues,
  print,
  onEdit,
  onDelete,
  onUndelete,
  canDelete = true,
}) => {
  const { density, tokens } = useDensity()
  const cellDensitySx = listDensityCellSx(tokens)
  const controlDensitySx = listDensityControlSx(density)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  })

  // Keep spacers in sync when the density-driven row height changes.
  useEffect(() => {
    virtualizer.measure()
  }, [rowHeight, virtualizer])

  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
  const paddingBottom =
    virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0
  const colSpan = columns.length + 1

  const renderGroupLabel = (entry: Extract<FlatEntry, { kind: 'group' }>): React.ReactNode => {
    const field = entry.path[entry.path.length - 1]?.field ?? ''
    const valuesRow = groupValues(entry.path) ?? { [field]: entry.label }
    const col = columns.find((c) => c.name === field)
    if (col) return renderListCell(col, valuesRow, hintFor(col))
    const display = valuesRow[`${field}_display`]
    const raw = display ?? entry.label
    return raw == null || raw === '' ? '—' : String(raw)
  }

  const renderGroupHeader = (entry: Extract<FlatEntry, { kind: 'group' }>, key: React.Key) => (
    <TableRow
      key={key}
      hover
      onClick={() => toggle(entry.path)}
      sx={{ cursor: 'pointer', bgcolor: 'action.hover', height: rowHeight }}
    >
      {columns.map((col, idx) => {
        if (idx === 0) {
          return (
            <TableCell key={col.name} sx={{ pl: 1 + 2 * entry.depth, ...cellDensitySx, ...listCellHairlineSx }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  sx={controlDensitySx}
                  aria-expanded={entry.expanded}
                  aria-label={entry.expanded ? 'Collapse group' : 'Expand group'}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggle(entry.path)
                  }}
                >
                  {entry.expanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
                  {renderGroupLabel(entry)}
                </Typography>
                <Chip label={entry.count} size="small" sx={{ height: 20, fontSize: 11 }} />
              </Box>
            </TableCell>
          )
        }
        const hint = hintFor(col)
        const numeric = isNumericListColumn(hint)
        const sum = entry.sums[col.name]
        const formatted =
          numeric && sum != null
            ? (() => {
                const { precision, scale } = resolveNumberDigits(hint?.props)
                return formatNumberDisplay(Number(sum), precision, scale)
              })()
            : null
        return (
          <TableCell
            key={col.name}
            sx={{ ...cellDensitySx, ...(col.mono ? monoSx : {}), ...listCellHairlineSx, ...listNumericBodySx(numeric) }}
          >
            {formatted != null && formatted !== '' ? (
              <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                {formatted}
              </Typography>
            ) : null}
          </TableCell>
        )
      })}
      <TableCell sx={{ ...cellDensitySx, ...listCellHairlineSx }} />
    </TableRow>
  )

  const renderSkeletonRow = (key: React.Key) => (
    <TableRow key={key} sx={{ height: rowHeight }}>
      {columns.map((col) => (
        <TableCell key={col.name} sx={listCellHairlineSx}>
          <Skeleton variant="text" width="80%" />
        </TableCell>
      ))}
      <TableCell align="right" sx={listCellHairlineSx}>
        <Skeleton variant="text" width="60%" />
      </TableCell>
    </TableRow>
  )

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ flex: 1, overflow: 'auto' }}
        ref={scrollRef as React.Ref<HTMLDivElement>}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.name} sx={{ ...widthSx(col.width), ...(col.mono ? monoSx : {}), ...listHeaderStickySx, ...listCellHairlineSx }}>
                  {col.title || col.name}
                </TableCell>
              ))}
              <TableCell
                align="right"
                aria-label="Row actions"
                sx={{ width: LIST_ROW_OVERFLOW_COL_WIDTH, ...listHeaderStickySx, ...listCellHairlineSx, textAlign: 'right' }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {paddingTop > 0 && (
              <TableRow style={{ height: paddingTop }}>
                <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }} />
              </TableRow>
            )}

            {virtualItems.map((virtualRow) => {
              const entry = entries[virtualRow.index]
              if (!entry) return null
              if (entry.kind === 'group') {
                return renderGroupHeader(entry, `g-${virtualRow.key}`)
              }
              const cell = rowAt(entry)
              if (cell.state !== 'loaded') {
                return renderSkeletonRow(`s-${virtualRow.key}`)
              }
              return (
                <ListDataRow
                  key={`r-${virtualRow.key}-${cell.row.id}`}
                  row={cell.row}
                  columns={columns}
                  hintFor={hintFor}
                  rowHeight={rowHeight}
                  print={print}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onUndelete={onUndelete}
                  canDelete={canDelete}
                />
              )
            })}

            {paddingBottom > 0 && (
              <TableRow style={{ height: paddingBottom }}>
                <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }} />
              </TableRow>
            )}

            {entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 0, border: 0 }}>
                  <ListEmptyState />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
